/**
 * Processing State Configuration
 * Centralized configuration for tender processing states
 */

import {
    CircleStackIcon,
    MagnifyingGlassIcon,
    ArrowPathIcon,
    DocumentArrowDownIcon,
    SparklesIcon,
    PlusCircleIcon,
    CogIcon,
    CheckIcon,
    ChartBarIcon,
    StarIcon,
    ArchiveBoxIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

// State configuration with all metadata
export const PROCESSING_STATES = {
    DRAFT: {
        label: 'Draft',
        color: 'gray',
        bgClass: 'bg-gray-700',
        textClass: 'text-gray-300',
        borderClass: 'border-gray-600',
        ringClass: 'ring-gray-500',
        Icon: CircleStackIcon,
        description: 'Initial state - minimal data from search/import',
        order: 0,
    },
    DISCOVERED: {
        label: 'Discovered',
        color: 'blue',
        bgClass: 'bg-blue-900',
        textClass: 'text-blue-300',
        borderClass: 'border-blue-700',
        ringClass: 'ring-blue-500',
        Icon: MagnifyingGlassIcon,
        description: 'Passed quality gate, ready for crawling',
        order: 1,
    },
    CRAWLING: {
        label: 'Crawling',
        color: 'yellow',
        bgClass: 'bg-yellow-900',
        textClass: 'text-yellow-300',
        borderClass: 'border-yellow-700',
        ringClass: 'ring-yellow-500',
        Icon: ArrowPathIcon,
        description: 'Crawl job in progress',
        isProcessing: true,
        order: 2,
    },
    CRAWLED: {
        label: 'Crawled',
        color: 'green',
        bgClass: 'bg-green-900',
        textClass: 'text-green-300',
        borderClass: 'border-green-700',
        ringClass: 'ring-green-500',
        Icon: DocumentArrowDownIcon,
        description: 'Documents downloaded from source',
        order: 3,
    },
    ENRICHING: {
        label: 'Enriching',
        color: 'yellow',
        bgClass: 'bg-yellow-900',
        textClass: 'text-yellow-300',
        borderClass: 'border-yellow-700',
        ringClass: 'ring-yellow-500',
        Icon: SparklesIcon,
        description: 'Deep crawl / geocoding in progress',
        isProcessing: true,
        isOptional: true,
        order: 4,
    },
    ENRICHED: {
        label: 'Enriched',
        color: 'green',
        bgClass: 'bg-green-900',
        textClass: 'text-green-300',
        borderClass: 'border-green-700',
        ringClass: 'ring-green-500',
        Icon: PlusCircleIcon,
        description: 'Enrichment complete (extra documents found)',
        isOptional: true,
        order: 5,
    },
    PROCESSING: {
        label: 'Processing',
        color: 'yellow',
        bgClass: 'bg-yellow-900',
        textClass: 'text-yellow-300',
        borderClass: 'border-yellow-700',
        ringClass: 'ring-yellow-500',
        Icon: CogIcon,
        description: 'Document conversion/text extraction in progress',
        isProcessing: true,
        order: 6,
    },
    PROCESSED: {
        label: 'Processed',
        color: 'green',
        bgClass: 'bg-green-900',
        textClass: 'text-green-300',
        borderClass: 'border-green-700',
        ringClass: 'ring-green-500',
        Icon: CheckIcon,
        description: 'Documents converted, text extracted',
        order: 7,
    },
    ASSESSING: {
        label: 'Assessing',
        color: 'yellow',
        bgClass: 'bg-yellow-900',
        textClass: 'text-yellow-300',
        borderClass: 'border-yellow-700',
        ringClass: 'ring-yellow-500',
        Icon: ChartBarIcon,
        description: 'AI assessment in progress',
        isProcessing: true,
        order: 8,
    },
    ASSESSED: {
        label: 'Assessed',
        color: 'emerald',
        bgClass: 'bg-emerald-800',
        textClass: 'text-white',
        borderClass: 'border-emerald-600',
        ringClass: 'ring-emerald-500',
        Icon: StarIcon,
        description: 'Assessment complete - fully processed',
        isTerminal: true,
        order: 9,
    },
    ARCHIVED: {
        label: 'Archived',
        color: 'gray',
        bgClass: 'bg-gray-800',
        textClass: 'text-gray-500',
        borderClass: 'border-gray-700',
        ringClass: 'ring-gray-600',
        Icon: ArchiveBoxIcon,
        description: 'Deadline passed, no longer active',
        isTerminal: true,
        order: 10,
    },
    ERROR: {
        label: 'Error',
        color: 'red',
        bgClass: 'bg-red-900',
        textClass: 'text-red-300',
        borderClass: 'border-red-700',
        ringClass: 'ring-red-500',
        Icon: ExclamationTriangleIcon,
        description: 'Failed - requires attention',
        isError: true,
        order: 11,
    },
};

// Main pipeline flow (excluding optional enrichment branch)
export const MAIN_FLOW_STATES = [
    'DRAFT',
    'DISCOVERED',
    'CRAWLING',
    'CRAWLED',
    'PROCESSING',
    'PROCESSED',
    'ASSESSING',
    'ASSESSED',
];

// States shown in filter chips
export const FILTER_STATES = [
    'DISCOVERED',
    'CRAWLED',
    'ENRICHED',
    'PROCESSED',
    'ASSESSED',
    'ERROR',
];

// State transitions
export const STATE_TRANSITIONS = [
    { from: 'DRAFT', to: 'DISCOVERED' },
    { from: 'DISCOVERED', to: 'CRAWLING' },
    { from: 'CRAWLING', to: 'CRAWLED' },
    { from: 'CRAWLING', to: 'ERROR' },
    { from: 'CRAWLED', to: 'ENRICHING', optional: true },
    { from: 'CRAWLED', to: 'PROCESSING' },
    { from: 'CRAWLED', to: 'ASSESSING' },
    { from: 'ENRICHING', to: 'ENRICHED' },
    { from: 'ENRICHING', to: 'ERROR' },
    { from: 'ENRICHED', to: 'PROCESSING' },
    { from: 'ENRICHED', to: 'ASSESSING' },
    { from: 'PROCESSING', to: 'PROCESSED' },
    { from: 'PROCESSING', to: 'ERROR' },
    { from: 'PROCESSED', to: 'ASSESSING' },
    { from: 'ASSESSING', to: 'ASSESSED' },
    { from: 'ASSESSING', to: 'ERROR' },
    { from: 'ERROR', to: 'DISCOVERED', isRetry: true },
    // Re-operations from terminal/processed states
    { from: 'ASSESSED', to: 'CRAWLING', isRedo: true },
    { from: 'ASSESSED', to: 'ENRICHING', isRedo: true },
    { from: 'ASSESSED', to: 'PROCESSING', isRedo: true },
    { from: 'ASSESSED', to: 'ASSESSING', isRedo: true },
];

// Actions available per state
export const STATE_ACTIONS = {
    DRAFT: ['crawl', 'deepCrawl'],
    DISCOVERED: ['crawl', 'deepCrawl'],
    CRAWLING: [],
    CRAWLED: ['deepCrawl', 'convert', 'assess'],
    ENRICHING: [],
    ENRICHED: ['convert', 'assess'],
    PROCESSING: [],
    PROCESSED: ['crawl', 'deepCrawl', 'convert', 'assess'],
    ASSESSING: [],
    ASSESSED: ['crawl', 'deepCrawl', 'convert', 'assess'],
    ERROR: ['crawl', 'deepCrawl'],
    ARCHIVED: [],
};

// Action configuration
export const ACTIONS = {
    crawl: {
        label: 'Crawl',
        description: 'Download documents from source',
        color: 'blue',
        Icon: ArrowPathIcon,
    },
    deepCrawl: {
        label: 'Deep Crawl',
        description: 'LLM-powered document extraction',
        color: 'cyan',
        Icon: SparklesIcon,
    },
    convert: {
        label: 'Convert',
        description: 'Extract text from documents',
        color: 'purple',
        Icon: CogIcon,
    },
    assess: {
        label: 'Assess',
        description: 'Run AI assessment',
        color: 'green',
        Icon: ChartBarIcon,
    },
};

/**
 * Get state info with defaults
 */
export const getStateInfo = (state) => {
    return PROCESSING_STATES[state] || PROCESSING_STATES.DRAFT;
};

/**
 * Get available actions for a state
 */
export const getAvailableActions = (state) => {
    return STATE_ACTIONS[state] || [];
};

/**
 * Check if state is a processing state (job in progress)
 */
export const isProcessingState = (state) => {
    return ['CRAWLING', 'ENRICHING', 'PROCESSING', 'ASSESSING'].includes(state);
};

/**
 * Check if state is terminal
 */
export const isTerminalState = (state) => {
    return ['ASSESSED', 'ARCHIVED'].includes(state);
};

/**
 * Check if state is an error state
 */
export const isErrorState = (state) => {
    return state === 'ERROR';
};

/**
 * Get the order index for a state (for timeline positioning)
 */
export const getStateOrder = (state) => {
    const info = getStateInfo(state);
    return info.order ?? 0;
};

/**
 * Determine if a state is "past" relative to another state
 * (simplified - doesn't handle enrichment branch)
 */
export const isStatePast = (state, currentState) => {
    const stateOrder = getStateOrder(state);
    const currentOrder = getStateOrder(currentState);
    return stateOrder < currentOrder;
};

/**
 * Calculate state counts from tender array
 */
export const calculateStateCounts = (tenders) => {
    const counts = {};

    // Initialize all states to 0
    Object.keys(PROCESSING_STATES).forEach(state => {
        counts[state] = 0;
    });

    // Count tenders per state
    tenders.forEach(tender => {
        const state = tender.processingState || 'DRAFT';
        if (counts[state] !== undefined) {
            counts[state]++;
        }
    });

    return counts;
};
